import useBranch from "@/shared/atoms/useBranch";
import { themeListAtom } from "@/shared/atoms";
import useCategoryQueryHook from "@/shared/hooks/useCategoryQueryHook";
import useFranchiseHook from "@/shared/hooks/useFranchiseHook";
import useGenerateImageUrl from "@/shared/hooks/useGenerateImageUrl";
import useThemesHook from "@/shared/hooks/useThemesHook";
import useWebsiteSettingsHook from "@/shared/hooks/useWebsiteSettingsHook";
import { errorToast } from "@/shared/utils/helpers";
import { IThemeItem } from "@/shared/utils/interfaces";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AllergenPosterBranch,
  AllergenPosterPage,
} from "../AllergenPoster/AllergenPoster";
import {
  A4_HEIGHT_PX,
  A4_WIDTH_PX,
  buildPosterRows,
  getPosterColors,
  paginatePosterRows,
  posterAllergens,
} from "../AllergenPoster/allergenPosterUtils";
import BooleanWithLabel from "../Boolean/BooleanWithLabel";
import Button from "../Button/Button";
import Textfield from "../Input/Textfield";
import Modal, { ModalProps } from "../Modal/Modal";
import Select from "../Select/Select";

const EMPTY_IMAGE_ID = "transpatentEmpty_sr5jbs";
const PREVIEW_SCALE = 0.5;

const slugify = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "restaurant";

const toCsv = (rows: string[][]) =>
  rows
    .map((row) =>
      row.map((cell) => `"${(cell ?? "").replace(/"/g, '""')}"`).join(";")
    )
    .join("\r\n");

const AllergenPosterModal = ({ onExit, ...props }: ModalProps) => {
  const { t } = useTranslation();
  useThemesHook();
  const themeList = useAtomValue(themeListAtom);
  const [franchiseId] = useFranchiseHook();
  const [branchDetails] = useBranch();
  const generateImageUrl = useGenerateImageUrl();
  const { websiteSettingsData } = useWebsiteSettingsHook({
    franchiseId,
    enabled: !!franchiseId,
  });
  const menuQuery = useCategoryQueryHook();

  const [selectedTheme, setSelectedTheme] = useState<IThemeItem | null>(null);
  const [title, setTitle] = useState<string>(t("allergenPoster.defaultTitle"));
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showLogo, setShowLogo] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Preselect the theme that is active on the ordering website
  useEffect(() => {
    if (selectedTheme || !themeList?.length) return;
    const activeTheme = themeList.find(
      (theme) => theme.item_id === +websiteSettingsData?.color_template_id
    );
    if (activeTheme) setSelectedTheme(activeTheme);
  }, [themeList, websiteSettingsData, selectedTheme]);

  const logoUrl = useMemo(() => {
    const logo = websiteSettingsData?.logo;
    if (!showLogo || !logo || logo === EMPTY_IMAGE_ID || !franchiseId) {
      return null;
    }
    return generateImageUrl(logo, "logo", franchiseId);
  }, [websiteSettingsData, showLogo, franchiseId, generateImageUrl]);

  const branch: AllergenPosterBranch = useMemo(() => {
    const street = [
      branchDetails?.street_name,
      [branchDetails?.house_number, branchDetails?.house_number_extension]
        .filter(Boolean)
        .join(""),
    ]
      .filter(Boolean)
      .join(" ");
    const city = [branchDetails?.zip_code, branchDetails?.place]
      .filter(Boolean)
      .join(" ");
    const phone = branchDetails?.telephone_number
      ? `${branchDetails?.telephone_country_prefix ?? ""} ${
          branchDetails.telephone_number
        }`.trim()
      : undefined;

    return {
      name: branchDetails?.name ?? "",
      address: [street, city].filter(Boolean).join(", "),
      phone,
    };
  }, [branchDetails]);

  const colors = useMemo(
    () => getPosterColors(selectedTheme ?? undefined),
    [selectedTheme]
  );
  const rows = useMemo(
    () =>
      buildPosterRows(menuQuery.data?.navigation_content, { includeInactive }),
    [menuQuery.data, includeInactive]
  );
  const pages = useMemo(() => paginatePosterRows(rows), [rows]);
  const generatedAt = dayjs().format("DD-MM-YYYY");
  const fileName = `${t("allergenPoster.fileName")}-${slugify(branch.name)}`;

  const pageProps = {
    colors,
    title,
    branch,
    logoUrl,
    generatedAt,
    pageCount: pages.length,
  };

  const downloadPdf = async () => {
    setIsExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all(
        [import("html2canvas"), import("jspdf")]
      );
      const pdf = new jsPDF("p", "mm", "a4");

      for (let index = 0; index < pages.length; index++) {
        const element = pageRefs.current[index];
        if (!element) continue;
        const canvas = await html2canvas(element, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#ffffff",
          width: A4_WIDTH_PX,
          height: A4_HEIGHT_PX,
        });
        if (index > 0) pdf.addPage("a4", "p");
        pdf.addImage(
          canvas.toDataURL("image/jpeg", 0.95),
          "JPEG",
          0,
          0,
          210,
          297
        );
      }

      pdf.save(`${fileName}.pdf`);
    } catch (error) {
      console.error(error);
      errorToast();
    } finally {
      setIsExporting(false);
    }
  };

  const downloadExcel = () => {
    const allergenHeaders = posterAllergens.map((a) => t(a.translationKey));
    const data: string[][] = [];
    let category = "";

    rows.forEach((row) => {
      if (row.type === "category") {
        category = row.name;
        return;
      }
      data.push([
        category,
        row.name,
        ...posterAllergens.map((a) => (row.allergens.includes(a.id) ? "X" : "")),
      ]);
    });

    // Semicolons + BOM so Excel (NL/BE locale) opens it in columns with accents intact
    const csv = toCsv([
      [
        t("allergenPoster.category"),
        t("allergenPoster.product"),
        ...allergenHeaders,
      ],
      ...data,
      [],
      [
        `${t("allergenPoster.disclaimerTitle")} ${t(
          "allergenPoster.disclaimer",
          { branch: branch.name }
        )}`,
      ],
    ]);
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal width={"110rem"} onExit={onExit} {...props}>
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col gap-5 lg:w-[32rem] lg:shrink-0">
          <div>
            <h2 className="text-[2rem] font-bold">{t("allergenPoster.modalTitle")}</h2>
            <p className="mt-2 text-small text-text-tertiary">
              {t("allergenPoster.modalDescription")}
            </p>
          </div>

          <Textfield
            label={t("allergenPoster.titleLabel")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <Select
            label={t("allergenPoster.theme")}
            options={themeList}
            value={selectedTheme}
            isClearable
            placeholder={t("allergenPoster.defaultTheme")}
            getOptionLabel={(option: IThemeItem) => option.template_name}
            getOptionValue={(option: IThemeItem) => `${option.item_id}`}
            onChange={(option: IThemeItem | null) => setSelectedTheme(option)}
          />

          <div className="flex gap-2">
            {[colors.primary, colors.accent].map((color, index) => (
              <span
                key={index}
                className="size-8 rounded-full border border-border"
                style={{ background: color }}
              />
            ))}
          </div>

          <BooleanWithLabel
            checked={showLogo}
            onChange={(e) => setShowLogo(e.target.checked)}
          >
            {t("allergenPoster.showLogo")}
          </BooleanWithLabel>

          <BooleanWithLabel
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          >
            {t("allergenPoster.includeInactive")}
          </BooleanWithLabel>

          <p className="rounded-lg bg-[#f3f4f6] p-3 text-small text-text-tertiary">
            {t("allergenPoster.checkNotice")}
          </p>

          <div className="flex flex-wrap gap-3">
            <Button
              icon="pdf"
              onClick={downloadPdf}
              isPending={isExporting}
              isDisabled={menuQuery.isPending}
            >
              {t("allergenPoster.downloadPdf")}
            </Button>
            <Button
              variant="secondary"
              onClick={downloadExcel}
              isDisabled={menuQuery.isPending || isExporting}
            >
              {t("allergenPoster.downloadExcel")}
            </Button>
            <Button variant="tertiary" onClick={() => onExit()}>
              {t("close")}
            </Button>
          </div>
        </div>

        <div className="max-h-[75vh] flex-1 overflow-auto rounded-lg bg-[#e5e7eb] p-4">
          <div className="flex flex-col items-center gap-4">
            {pages.map((pageRows, index) => (
              <div
                key={index}
                className="shadow-lg"
                style={{
                  width: A4_WIDTH_PX * PREVIEW_SCALE,
                  height: A4_HEIGHT_PX * PREVIEW_SCALE,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    transform: `scale(${PREVIEW_SCALE})`,
                    transformOrigin: "top left",
                  }}
                >
                  <AllergenPosterPage
                    rows={pageRows}
                    pageIndex={index}
                    {...pageProps}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Unscaled copies used for the PDF capture, kept off screen */}
      <div
        aria-hidden
        style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }}
      >
        {pages.map((pageRows, index) => (
          <AllergenPosterPage
            key={index}
            ref={(element) => (pageRefs.current[index] = element)}
            rows={pageRows}
            pageIndex={index}
            {...pageProps}
          />
        ))}
      </div>
    </Modal>
  );
};

export default AllergenPosterModal;
